import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/CognitoAuthContext";
import PalliumPageBackground from "../layout/PalliumPageBackground";
import PalliumCard from "../layout/PalliumCard";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { error, isLoading, forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setLocalError(null);
    setSuccessMessage(null);
    
    try {
      await forgotPassword(email);
      setSuccessMessage("Reset code sent to your email address");
      navigate(`/reset-password?email=${encodeURIComponent(email)}`, { replace: true });
    } catch (error) {
      console.error('Forgot password error:', error);
      setLocalError(error.message || "There was a problem sending the code to your email address");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Input field styles
  const inputBaseStyle = {
    border: '1px solid var(--pallium-border-default)',
    borderRadius: 'var(--pallium-border-radius-md)',
    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
  };

  const handleInputFocus = (e) => {
    e.target.style.borderColor = 'var(--pallium-border-focus)';
    e.target.style.boxShadow = 'var(--pallium-focus-ring-primary)';
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = 'var(--pallium-border-default)';
    e.target.style.boxShadow = 'none';
  };

  return (
    <PalliumPageBackground>
      <PalliumCard centered width="narrow" padding="large">
        <div className="flex justify-center mb-6">
          <img 
            src="/pallium-logo-blue.svg" 
            alt="Pallium Logo" 
            className="h-12 w-auto"
          />
        </div>
        <h2 
          className="text-2xl font-bold text-center mb-6"
          style={{ color: 'var(--pallium-primary-dark-navy)' }}
        >
          Forgot Password?
        </h2>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label 
              className="block mb-1 text-sm font-medium"
              style={{ color: 'var(--pallium-text-tertiary)' }}
            >
              Email address
            </label>
            <input
              type="email"
              className="w-full px-4 py-3 focus:outline-none"
              style={inputBaseStyle}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              required
              disabled={isSubmitting || isLoading}
            />
          </div>
          <div>
            <p 
              className="text-sm"
              style={{ color: 'var(--pallium-text-secondary)' }}
            >
              A code will be sent to the email address if it is registered
            </p>
          </div>
          {(error || localError) && (
            <div 
              className="p-3 rounded-md text-sm"
              style={{ 
                backgroundColor: 'var(--pallium-alert-error-bg)', 
                color: 'var(--pallium-error-red)',
                border: '1px solid var(--pallium-error-red)'
              }}
            >
              {localError || error}
            </div>
          )}
          {successMessage && (
            <div 
              className="p-3 rounded-md text-sm"
              style={{ 
                backgroundColor: 'rgba(19, 185, 85, 0.1)', 
                color: 'var(--pallium-success-green)',
                border: '1px solid var(--pallium-success-green)'
              }}
            >
              {successMessage}
            </div>
          )}
          <button
            type="submit"
            disabled={isSubmitting || isLoading}
            className="w-full py-3 rounded-md text-white font-medium transition-colors duration-200 disabled:cursor-not-allowed"
            style={{ 
              backgroundColor: isSubmitting || isLoading ? 'var(--pallium-button-primary-disabled)' : 'var(--pallium-button-primary-bg)',
              borderRadius: 'var(--pallium-border-radius-md)'
            }}
            onMouseOver={(e) => { if (!isSubmitting && !isLoading) e.target.style.backgroundColor = 'var(--pallium-button-primary-hover)'; }}
            onMouseOut={(e) => { if (!isSubmitting && !isLoading) e.target.style.backgroundColor = 'var(--pallium-button-primary-bg)'; }}
          >
            {isSubmitting ? 'Sending Code...' : 'Send Code'}
          </button>
          <div className="text-center">
            <button 
              type="button"
              className="text-sm cursor-pointer hover:underline bg-transparent border-none p-0"
              style={{ color: 'var(--pallium-link-default)' }}
              onClick={() => navigate('/')}
              onMouseOver={(e) => e.target.style.color = 'var(--pallium-link-hover)'}
              onMouseOut={(e) => e.target.style.color = 'var(--pallium-link-default)'}
            >
              Back to Login
            </button>
          </div>
        </form>
      </PalliumCard>
    </PalliumPageBackground>
  );
};

export default ForgotPassword;
